import moment from "moment";
import { CARD_TYPES } from "./cardTypes";
import { translate } from "../i18n/messages";

// Texts handed to an AI assistant (ChatGPT, Claude, Copilot, ...): a prompt to plan
// a new agenda, and one to change the current plan. Both explain the short
// link format inline, because not every assistant can open llms.txt.

export const SITE = "https://focussuite.de";

export const ASSISTANTS = [
  {
    name: "ChatGPT",
    url: (prompt) => `https://chatgpt.com/?q=${encodeURIComponent(prompt)}`,
  },
  {
    name: "Claude",
    url: (prompt) => `https://claude.ai/new?q=${encodeURIComponent(prompt)}`,
  },
  // Microsoft 365 Copilot Chat, the one most companies use with their work
  // account. Prefilling via ?q= isn't documented for it, so the prompt is
  // also copied on click and can be pasted if the chat opens empty.
  {
    name: "Copilot",
    url: (prompt) =>
      `https://m365.cloud.microsoft/chat/?q=${encodeURIComponent(prompt)}`,
    copyFirst: true,
  },
];

function durationToken(time) {
  const seconds = Math.round(moment.duration(time).asSeconds());
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (!rest) {
    return String(minutes);
  }
  return minutes ? `${minutes}m${rest}s` : `${rest}s`;
}

// "|" separates cards and "~" the parts of a card, so they can't
// appear inside a text; everything else is percent-encoded, with spaces
// as "+".
function textToken(text) {
  return encodeURIComponent(
    text.replace(/[|]/g, "/").replace(/~/g, "-")
  ).replace(/%20/g, "+");
}

function cardToken(card) {
  const parts = [textToken(card.topic || "Untitled"), durationToken(card.time)];
  ["type", "owner", "note"].forEach((key) => {
    if (card[key]) {
      parts.push(`${key}:${textToken(card[key])}`);
    }
  });
  return parts.join("~");
}

// The current plan as a short link (see public/llms.txt).
// With absolute, the start is written in UTC so the plan starts at the
// same moment for everyone who opens it, whatever their time zone; without
// it, it's the local clock time an assistant can read and edit easily.
export function buildShortLink(
  starttime,
  agenda,
  base = SITE,
  { absolute = false, page = "" } = {}
) {
  const plan = agenda.map(cardToken).join("|");
  const start = absolute
    ? starttime.clone().utc().format("YYYY-MM-DDTHH:mm:ss[Z]")
    : starttime.format("YYYY-MM-DDTHH:mm");
  return `${base}/#/${page}?start=${start}&plan=${plan}`;
}

// The current plan as a readable list, one card per line with its times.
export function formatPlanAsText(starttime, agenda, language = "en") {
  const t = (key, params) => translate(language, key, params);
  let cursor = starttime.clone();
  return agenda
    .map((card) => {
      const start = cursor.clone();
      cursor = cursor.clone().add(moment.duration(card.time));
      const minutes = Math.round(moment.duration(card.time).asMinutes());
      const details = [
        t("exports.minutes", { minutes }),
        CARD_TYPES[card.type] && t(`cardTypes.${card.type}`),
        card.owner && t("exports.owner", { name: card.owner }),
      ].filter(Boolean);
      const line = `- ${start.format("HH:mm")}–${cursor.format("HH:mm")} ${
        card.topic || t("common.untitled")
      } (${details.join(", ")})`;
      return card.note
        ? `${line}\n  ${t("exports.note", { note: card.note })}`
        : line;
    })
    .join("\n");
}

const FORMAT = {
  en: `How to build the link (full guide: ${SITE}/llms.txt):
${SITE}/#/?start=09:00&plan=Welcome~10|Brainstorming~25|Break~15
- start: local time, e.g. 09:00 for today or 2026-10-20T09:00 for a date. Don't convert to UTC.
- plan: cards separated by |, each as Topic~Duration. Duration in minutes (or 1h30, 90m).
- Write spaces in topics as +. Other special characters as UTF-8 percent-encoding (é = %C3%A9, ü = %C3%BC, & = %26). Don't use | or ~ inside a topic.
- Cards run back to back, so add breaks and buffers as their own cards.
- Optional details per card, each after another ~: type:break|input|work|discussion|workout, owner:Name, note:Short+note
  Example: Brainstorming~25~type:work~owner:Anna~note:Post-its+on+the+wall`,
  de: `So baust du den Link (vollständige Anleitung: ${SITE}/llms.txt):
${SITE}/#/?start=09:00&plan=Begr%C3%BC%C3%9Fung~10|Brainstorming~25|Pause~15
- start: Ortszeit, z. B. 09:00 für heute oder 2026-10-20T09:00 für ein Datum. Nicht in UTC umrechnen.
- plan: Karten mit | getrennt, jede als Thema~Dauer. Dauer in Minuten (oder 1h30, 90m).
- Leerzeichen im Thema als +, Umlaute und ß als UTF-8 percent-encoded: ä = %C3%A4, ö = %C3%B6, ü = %C3%BC, Ä = %C3%84, Ö = %C3%96, Ü = %C3%9C, ß = %C3%9F, & = %26. Kein | oder ~ im Thema.
- Karten laufen direkt hintereinander, Pausen und Puffer also als eigene Karten einplanen.
- Optionale Details pro Karte, jeweils nach einem weiteren ~: type:break|input|work|discussion|workout (Pause, Input, Gruppenarbeit, Diskussion, Sportübung), owner:Name, note:Kurze+Notiz
  Beispiel: Brainstorming~25~type:work~owner:Anna~note:Post-its+an+die+Wand`,
};

export function buildPlanPrompt(language = "en") {
  if (language === "de") {
    return `Hilf mir, einen Termin mit Timeboxing zu planen (Meeting, Workshop, Lerneinheit, ...). Am Ende brauche ich den fertigen Plan als Timer-Link für focussuite.de.

Falls ich es noch nicht gesagt habe, frag mich zuerst kurz: Worum geht es, wann geht es los, wie lange soll es dauern und wer ist dabei? Schlag dann einen Ablauf vor. Zeig den Plan als Liste mit Uhrzeiten und gib danach den Link aus.

${FORMAT.de}

Mein Termin: `;
  }
  return `Help me plan a timeboxed session (meeting, workshop, study block, ...). At the end I need the finished plan as a timer link for focussuite.de.

If I haven't said yet, first ask me briefly: what is it about, when does it start, how long should it take and who is attending? Then propose an agenda. Show the plan as a list with times, then give me the link.

${FORMAT.en}

My session: `;
}

export function buildEditPrompt(
  starttime,
  agenda,
  language = "en",
  base = SITE
) {
  const plan = formatPlanAsText(starttime, agenda, language);
  const link = buildShortLink(starttime, agenda, base);
  if (language === "de") {
    return `Das ist mein aktueller Timeboxing-Plan (focussuite.de), Start ${starttime.format(
      "DD.MM.YYYY HH:mm"
    )}:
${plan}

Aktueller Link: ${link}

Bitte ändere den Plan so, wie ich es unten beschreibe. Zeig den neuen Plan als Liste mit Uhrzeiten und gib danach den neuen Link im selben Format aus.

${FORMAT.de}

Meine Änderung: `;
  }
  return `This is my current timeboxed plan (focussuite.de), starting ${starttime.format(
    "YYYY-MM-DD HH:mm"
  )}:
${plan}

Current link: ${link}

Please change the plan as I describe below. Show the new plan as a list with times, then give me the new link in the same format.

${FORMAT.en}

My change: `;
}
